import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { DomService } from '@rd/core/services/dom/dom.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { SHORT_BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { WestpacRedirectService } from '../../services/redirect.service';
import { WestpacNavLink } from '../../types';

@Component({
  selector: 'rd-westpac-mobile-header',
  templateUrl: './mobile-header.component.html',
  styleUrls: ['./mobile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WestpacMobileHeaderComponent {
  @HostBinding('class.rd-westpac-mobile-header') componentClass = true;
  @ViewChild('sidenav') sideNavRef: MatSidenav;
  isSideNavOpen = false;

  sideNavLinks: WestpacNavLink[] = [
    {
      name: 'wl.westpac.header.sidemenu.home',
      url: '/',
    },
    {
      name: 'wl.westpac.header.sidemenu.special_offers',
      url: '/offers',
    },
    {
      name: 'wl.westpac.header.sidemenu.hotels',
      url: this.urlUtilsService.travelEdgeUrl().toString(),
    },
    {
      name: 'wl.westpac.header.sidemenu.gift_cards',
      url: '/gift_card',
    },
  ];

  sideNavManageLinks: WestpacNavLink[] = [
    {
      name: 'wl.westpac.header.sidemenu.manage_hotel_bookings',
      url: this.urlUtilsService.travelEdgeUrl('/account/booking').toString(),
    },
    {
      name: 'wl.westpac.header.sidemenu.manage_gift_cards',
      url: '/gift_card/manage',
    },
  ];

  sideNavHelpLinks: WestpacNavLink[] = [
    {
      name: 'wl.westpac.header.sidemenu.faq',
      url: this.urlUtilsService
        .travelEdgeUrl(`/company/${this.shortBrandName.toLowerCase()}-faqs`)
        .toString(),
    },
    {
      name: 'wl.westpac.header.sidemenu.tncs',
      url: this.urlUtilsService
        .travelEdgeUrl(
          `/terms/${this.shortBrandName.toLowerCase()}-terms-and-conditions`,
        )
        .toString(),
    },
    {
      name: 'wl.westpac.header.sidemenu.privacy_policy',
      url: this.urlUtilsService
        .travelEdgeUrl(
          `/terms/${this.shortBrandName.toLowerCase()}-privacy-policy`,
        )
        .toString(),
    },
    {
      name: 'wl.westpac.header.sidemenu.contact_us',
      url: this.urlUtilsService.travelEdgeUrl('/company/contact_us').toString(),
    },
  ];

  constructor(
    @Inject(SHORT_BRAND_NAME) private shortBrandName: string,
    protected urlUtilsService: UrlUtilsService,
    protected iconRegistryService: IconRegistryService,
    protected domService: DomService,
    protected router: Router,
    protected redirectService: WestpacRedirectService,
  ) {
    this.registerIcons();
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('close');
  }

  openSideNav(): void {
    this.isSideNavOpen = true;
    this.sideNavRef.open();
  }

  closeSideNav(): void {
    this.isSideNavOpen = false;
    this.sideNavRef.close();
  }

  handleSideNavOpen(): void {
    this.domService.lockBody();
  }

  handleSideNavClose(): void {
    this.domService.unlockBody();
  }

  style(): Record<string, string> {
    if (!this.isSideNavOpen) {
      return {
        'pointer-events': 'none',
      };
    }
    return {};
  }

  logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/header-logo-mobile.png');
  }

  getBrandLogoName(): string {
    return `${this.shortBrandName.toLowerCase()}-logo`;
  }

  isActiveNavLink(sideNavLink: WestpacNavLink): boolean {
    const url = this.router.url;

    if (sideNavLink.url.startsWith('/offers') && url.startsWith('/offers')) {
      return true;
    }

    if (
      sideNavLink.url.startsWith('/gift_card/manage') &&
      url.startsWith('/gift_card/manage')
    ) {
      return true;
    }

    if (
      sideNavLink.url.startsWith('/gift_card') &&
      url.startsWith('/gift_card')
    ) {
      // For manage GC (/gift_card/manage), don't set "Gift Cards" as active, must be "Manage Gift Cards"
      if (
        sideNavLink.url.startsWith('/gift_card/manage') ||
        url.startsWith('/gift_card/manage')
      ) {
        return false;
      }

      return true;
    }

    // Handle "homepage" case
    if (sideNavLink.url === url) {
      return true;
    }

    return false;
  }

  isExternalNavLink(navLink: WestpacNavLink): boolean {
    return (
      navLink.url.toString().startsWith('http://') ||
      navLink.url.toString().startsWith('https://')
    );
  }
}
